import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Shield, X, ChevronDown, ChevronUp, Cookie } from 'lucide-react';
import { Button } from './ui/button';
import {
  getConsent,
  setConsent,
  acceptAll,
  rejectAll,
  hasConsented,
} from '../utils/cookieConsent';

const CookieConsent = () => {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    // Show banner only if user hasn't consented yet
    const timer = setTimeout(() => {
      if (!hasConsented()) {
        setVisible(true);
      }
    }, 800);
    return () => clearTimeout(timer);
  }, []);

  const handleAcceptAll = () => {
    acceptAll();
    setVisible(false);
  };

  const handleRejectAll = () => {
    rejectAll();
    setVisible(false);
  };

  const handleSavePreferences = () => {
    setConsent(preferences);
    setVisible(false);
  };

  const toggleCategory = (category) => {
    if (category === 'essential') return; // Can't disable essential
    setPreferences((prev) => ({ ...prev, [category]: !prev[category] }));
  };

  if (!visible) return null;

  return (
    <>
      {/* Slim full-width bar — no backdrop overlay */}
      <div
        className="fixed bottom-0 left-0 right-0 z-[9999] animate-in slide-in-from-bottom duration-300"
        data-testid="cookie-consent-banner"
      >
        <div className="bg-slate-900/95 backdrop-blur-sm">
          {/* Expandable Details */}
          {showDetails && (
            <div className="border-b border-white/10 px-4 py-2.5 max-w-screen-xl mx-auto" data-testid="cookie-details-panel">
              <div className="flex flex-wrap gap-3 items-center">
                <div className="flex items-center gap-2">
                  <span className="text-[11px] text-slate-300">{t('cookie.essential')}</span>
                  <span className="text-[10px] text-green-400">{t('cookie.alwaysActive')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[11px] text-slate-300">{t('cookie.analytics')}</span>
                  <button
                    onClick={() => toggleCategory('analytics')}
                    className={`relative w-7 h-4 rounded-full transition-colors ${preferences.analytics ? 'bg-blue-500' : 'bg-slate-600'}`}
                    data-testid="cookie-toggle-analytics"
                    aria-label="Toggle analytics cookies"
                  >
                    <span className={`absolute top-[2px] left-[2px] w-3 h-3 bg-white rounded-full shadow transition-transform ${preferences.analytics ? 'translate-x-3' : ''}`} />
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[11px] text-slate-300">{t('cookie.marketing')}</span>
                  <button
                    onClick={() => toggleCategory('marketing')}
                    className={`relative w-7 h-4 rounded-full transition-colors ${preferences.marketing ? 'bg-blue-500' : 'bg-slate-600'}`}
                    data-testid="cookie-toggle-marketing"
                    aria-label="Toggle marketing cookies"
                  >
                    <span className={`absolute top-[2px] left-[2px] w-3 h-3 bg-white rounded-full shadow transition-transform ${preferences.marketing ? 'translate-x-3' : ''}`} />
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Main bar */}
          <div className="max-w-screen-xl mx-auto px-4 py-2 flex items-center gap-3 flex-wrap sm:flex-nowrap">
            <p className="text-[11px] text-slate-400 flex-1 min-w-0 leading-snug">
              {t('cookie.description')}{' '}
              <Link
                to="/politique-de-confidentialite"
                className="text-blue-400 hover:text-blue-300 underline underline-offset-2"
                onClick={() => setVisible(false)}
              >
                {t('cookie.privacyLink')}
              </Link>
            </p>

            <div className="flex items-center gap-1.5 flex-shrink-0">
              <button
                onClick={() => setShowDetails(!showDetails)}
                className="text-[11px] text-slate-500 hover:text-slate-300 transition-colors px-2 py-1"
                data-testid="cookie-customize-btn"
              >
                {t('cookie.customize')}
              </button>

              {showDetails ? (
                <button
                  onClick={handleSavePreferences}
                  className="bg-blue-600 hover:bg-blue-500 text-white rounded-md text-[11px] h-6 px-3 transition-colors"
                  data-testid="cookie-save-btn"
                >
                  {t('cookie.savePreferences')}
                </button>
              ) : (
                <>
                  <button
                    onClick={handleRejectAll}
                    className="border border-slate-600 text-slate-300 hover:bg-slate-800 rounded-md text-[11px] h-6 px-3 transition-colors"
                    data-testid="cookie-reject-btn"
                  >
                    {t('cookie.rejectAll')}
                  </button>
                  <button
                    onClick={handleAcceptAll}
                    className="bg-blue-600 hover:bg-blue-500 text-white rounded-md text-[11px] h-6 px-3 transition-colors"
                    data-testid="cookie-accept-btn"
                  >
                    {t('cookie.acceptAll')}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

// Small floating button to re-open cookie preferences (shown in footer)
export const CookieSettingsButton = ({ onClick }) => {
  const { t } = useTranslation();
  return (
    <button
      onClick={onClick}
      className="text-slate-400 hover:text-[#3b82f6] transition-colors text-sm flex items-center gap-1.5"
      data-testid="cookie-settings-footer-btn"
    >
      <Cookie className="h-3.5 w-3.5" />
      {t('cookie.managePreferences')}
    </button>
  );
};

export default CookieConsent;
